"use client";

import React, { useEffect, useState } from "react";
import { useNav } from "../../components/NavigationProvider";
import Spinner from "../../components/Spinner";
import { PORTAL_THEME } from "../../../lib/portal_theme";
import { setNilStoreCookie } from "../../../lib/nil_fields.js";

const T = PORTAL_THEME;

const CARD = {
  border: T.cardEdge, boxShadow: "none", borderRadius: 0, background: T.cardBackground,
  padding: "18px 20px", color: T.text, fontFamily: T.fontFamily,
};
const BTN = {
  padding: "11px 20px", borderRadius: 0, textTransform: "uppercase", letterSpacing: "1px", border: T.chromeBorder, background: T.chromeGradient,
  color: T.chromeButtonText, fontWeight: 700, fontSize: 14, fontFamily: T.fontFamily, cursor: "pointer", boxShadow: T.chromeButtonShadow, textDecoration: "none",
};
const GHOST = { ...BTN, background: "transparent", color: T.text, boxShadow: "none", border: `1px solid ${T.cardBorder}` };
const INPUT = {
  padding: "10px 12px", borderRadius: 0, border: `1px solid ${T.buttonBorder}`, background: T.pageBackground,
  color: T.text, fontFamily: T.fontFamily, fontSize: 14, boxSizing: "border-box",
};

// Team store home, right after sign-in (/team → /signin → here): the
// customer's team stores — manage one, or create a design for it — and a
// form to open another. Static segment, so it wins over /team/[slug] (and
// "start" is a reserved team slug).
export default function TeamStartPage() {
  const nav = useNav();
  const [stores, setStores] = useState(null);
  const [teamName, setTeamName] = useState("");
  const [creating, setCreating] = useState({ busy: false, error: "" });

  useEffect(() => {
    let cancelled = false;
    fetch("/api/nil/store", { cache: "no-store", credentials: "include" })
      .then((r) => r.json().then((d) => ({ status: r.status, d })))
      .then(({ status, d }) => {
        if (cancelled) return;
        if (status === 401) { window.location.href = "/team?start=1"; return; }
        setStores((d?.stores || []).filter((s) => s.kind === "team"));
      })
      .catch(() => { if (!cancelled) setStores([]); });
    return () => { cancelled = true; };
  }, []);

  // Add to Store fills this team's store until they go somewhere else.
  const design = (slug) => { setNilStoreCookie(slug); nav.push("/service?flow=print&nil=1"); };

  const create = async (e) => {
    e.preventDefault();
    setCreating({ busy: true, error: "" });
    const r = await fetch("/api/nil/store", {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind: "team", teamName }),
    }).catch(() => null);
    const d = await r?.json().catch(() => ({}));
    if (!d?.ok) { setCreating({ busy: false, error: d?.message || "Couldn't open the store. Try again." }); return; }
    setStores((s) => [...(s || []), d.store]);
    setTeamName("");
    setCreating({ busy: false, error: "" });
  };

  if (!stores) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, fontFamily: T.fontFamily, color: T.textMuted }}>
        <Spinner size={18} />Loading…
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", fontFamily: T.fontFamily, color: T.text }}>
      <div style={{ maxWidth: 820, margin: "0 auto", padding: "48px 20px" }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, margin: "0 0 6px", letterSpacing: "-0.5px" }}>Your Team Stores</h1>
        <p style={{ color: T.textMuted, fontSize: 14, margin: "0 0 28px" }}>
          {stores.length ? "Manage a store, or create a new design for it." : "Open a store for your team, then create designs for it."}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 28 }}>
          {stores.map((s) => (
            <div key={s.slug} style={{ ...CARD, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 18 }}>{s.name}</div>
                <div style={{ color: T.textMuted, fontSize: 13, wordBreak: "break-all" }}>{s.url.replace(/^https?:\/\//, "")}</div>
              </div>
              <a href={s.path} style={GHOST}>Manage Store</a>
              <button type="button" onClick={() => design(s.slug)} style={BTN}>Create a Design</button>
            </div>
          ))}
        </div>

        <form onSubmit={create} style={{ ...CARD, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>{stores.length ? "Open another team store" : "Open a team store"}</div>
          <div style={{ color: T.textMuted, fontSize: 13, lineHeight: 1.6 }}>
            Use the name buyers know the team by, e.g. &quot;Canisius Lacrosse&quot;. It becomes the store&apos;s address and the start of every product name.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="Team name" maxLength={60} style={{ ...INPUT, flex: "1 1 240px", minWidth: 0 }} />
            <button type="submit" disabled={creating.busy || !teamName.trim()} style={{ ...BTN, opacity: creating.busy || !teamName.trim() ? 0.6 : 1 }}>
              {creating.busy ? "Opening…" : "Open Store"}
            </button>
          </div>
          {creating.error && <div style={{ color: "#f87171", fontSize: 13 }}>{creating.error}</div>}
        </form>

        <p style={{ color: T.textMuted, fontSize: 13, margin: "24px 0 0" }}>
          <a href="/team/about" style={{ color: T.text }}>How team stores work →</a>
        </p>
      </div>
    </div>
  );
}
